import { QuestionItem } from "../types/form";
import { isEmptyAnswer } from "./answerEmpty";
import {
  getValidationFormat,
  getValidationRules,
  hasRequiredValue,
} from "./validationRules";
import { validateUsSsn } from "./usSsnValidation";
import { validateUsPhone } from "./usPhoneValidation";
import { validateUsANumber } from "./usANumberValidation";
import { validateUscisAccount } from "./uscisAccountValidation";
import { validateEmail } from "./emailValidation";
import { validateUsWeightLbs } from "./usWeightLbsValidation";
import { hasDateBounds, validateDateRange } from "./dateRangeValidation";
import { validateCompositeAnswer } from "./compositeValidation";
import { normalizeCountryAnswer } from "./countries";
import { validateNonNegativeInteger } from "./nonNegativeIntegerValidation";

/**
 * Validate a single answer against question.validationRules.
 * Returns a user-facing error message, or null when valid / not applicable.
 */
export function validateAnswer(
  question: QuestionItem,
  value: unknown,
  answers?: Record<string, unknown>,
): string | null {
  const answerType = question.answerType ?? question.type;
  if (answerType === "composite") {
    const rules = getValidationRules(question);
    const hasExactCount = Number(rules?.exactEntryCountFrom?.questionId) > 0;
    if (isEmptyAnswer(value) && !hasExactCount) return null;
    return validateCompositeAnswer(question, value, answers);
  }

  if (isEmptyAnswer(value)) return null;

  // Mirrors the server's requiredValue rule: e.g. N-400 12.c, the SSA consent,
  // must be Yes whenever it is shown (12.a Yes). Case-insensitive.
  const fixedRules = getValidationRules(question);
  if (fixedRules && hasRequiredValue(fixedRules)) {
    const expected = String(fixedRules.requiredValue).trim().toLowerCase();
    if (String(value).trim().toLowerCase() !== expected) {
      return (
        fixedRules.requiredValueMessage || "This answer is not allowed here."
      );
    }
  }

  if (answerType === "country") {
    const values = Array.isArray(value) ? value : [value];
    return values.every((entry) => normalizeCountryAnswer(entry))
      ? null
      : "Select a valid country.";
  }
  const format = getValidationFormat(question);
  if (format === "us_ssn") {
    const result = validateUsSsn(value);
    return result.ok ? null : result.message;
  }

  if (format === "us_phone") {
    const result = validateUsPhone(value);
    return result.ok ? null : result.message;
  }

  if (format === "us_a_number") {
    const result = validateUsANumber(value);
    return result.ok ? null : result.message;
  }

  if (format === "uscis_account") {
    const result = validateUscisAccount(value);
    return result.ok ? null : result.message;
  }

  if (format === "email") {
    const result = validateEmail(value);
    return result.ok ? null : result.message;
  }

  if (format === "us_weight_lbs") {
    const result = validateUsWeightLbs(value);
    return result.ok ? null : result.message;
  }

  if (format === "non_negative_integer") {
    const result = validateNonNegativeInteger(value);
    return result.ok ? null : result.message;
  }

  const rules = getValidationRules(question);
  if (rules && hasDateBounds(rules)) {
    const result = validateDateRange(value, rules, new Date(), answers);
    return result.ok ? null : result.message;
  }

  return null;
}
